import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "./context/LanguageContext";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ParentAuthProvider } from "./context/ParentAuthContext";
import { UpgradeProvider } from "./context/UpgradeContext";
import { ActivationProvider } from "./context/ActivationContext";
import { Navbar } from "./components/Navbar";
import { initPixel, trackPageView, trackWelcomeIfPresent } from "./lib/metaPixel";
import OfflineIndicator from "./components/OfflineIndicator";
import AppLayout from "./components/AppLayout";
import "./App.css";

// Code-splitting: every route page is lazy-loaded so the initial bundle stays
// tiny (critical for low-bandwidth African mobile networks). Named exports are
// adapted to default via `.then(m => ({ default: m.X }))`.
const Landing = lazy(() => import("./pages/Landing").then((m) => ({ default: m.Landing })));
const Login = lazy(() => import("./pages/Login").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("./pages/Register").then((m) => ({ default: m.Register })));
const Dashboard = lazy(() => import("./pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const SchoolForm = lazy(() => import("./pages/SchoolForm").then((m) => ({ default: m.SchoolForm })));
const GeneratedWebsite = lazy(() => import("./pages/GeneratedWebsite").then((m) => ({ default: m.GeneratedWebsite })));
const AdminPanel = lazy(() => import("./pages/AdminPanel").then((m) => ({ default: m.AdminPanel })));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then((m) => ({ default: m.ResetPassword })));
const Inbox = lazy(() => import("./pages/Inbox").then((m) => ({ default: m.Inbox })));
const Council = lazy(() => import("./pages/Council").then((m) => ({ default: m.Council })));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard").then((m) => ({ default: m.TeacherDashboard })));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard").then((m) => ({ default: m.StudentDashboard })));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard").then((m) => ({ default: m.ParentDashboard })));
const ClaimAccount = lazy(() => import("./pages/ClaimAccount").then((m) => ({ default: m.ClaimAccount })));
const SchoolSetupWizard = lazy(() => import("./pages/SchoolSetupWizard").then((m) => ({ default: m.SchoolSetupWizard })));
const AcademicYears = lazy(() => import("./pages/AcademicYears").then((m) => ({ default: m.AcademicYears })));
const SchoolStructure = lazy(() => import("./pages/SchoolStructure").then((m) => ({ default: m.SchoolStructure })));
const EnhancedStudents = lazy(() => import("./pages/EnhancedStudents").then((m) => ({ default: m.EnhancedStudents })));
const SchoolCalendar = lazy(() => import("./pages/SchoolCalendar").then((m) => ({ default: m.SchoolCalendar })));
const AuditLog = lazy(() => import("./pages/AuditLog").then((m) => ({ default: m.AuditLog })));
const EnhancedDashboard = lazy(() => import("./pages/EnhancedDashboard").then((m) => ({ default: m.EnhancedDashboard })));
const AttendanceMarking = lazy(() => import("./pages/AttendanceMarking"));
const TimetableManagement = lazy(() => import("./pages/TimetableManagement"));
const CommunicationHub = lazy(() => import("./pages/CommunicationHub"));
const FeeManagement = lazy(() => import("./pages/FeeManagement"));
const GradeBook = lazy(() => import("./pages/GradeBook"));
const HomeworkManagement = lazy(() => import("./pages/HomeworkManagement"));
const CourseContent = lazy(() => import("./pages/CourseContent"));
const ReportCards = lazy(() => import("./pages/ReportCards"));
const StudentHomework = lazy(() => import("./pages/StudentHomework"));
const StudentGrades = lazy(() => import("./pages/StudentGrades"));
const QuizBuilder = lazy(() => import("./pages/QuizBuilder"));
const QuizTaking = lazy(() => import("./pages/QuizTaking"));
const OfflineContent = lazy(() => import("./pages/OfflineContent"));
const SchoolAnalytics = lazy(() => import("./pages/SchoolAnalytics"));
const TeacherAnalytics = lazy(() => import("./pages/TeacherAnalytics"));
const ParentProgressReport = lazy(() => import("./pages/ParentProgressReport"));
const UBOCertification = lazy(() => import("./pages/UBOCertification"));
const DataExport = lazy(() => import("./pages/DataExport"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const ParentChatPage = lazy(() => import("./pages/ParentChatPage"));
const AdminParentInbox = lazy(() => import("./pages/AdminParentInbox"));
const AdmissionsOffice = lazy(() => import("./pages/AdmissionsOffice"));
const CouncilHub = lazy(() => import("./pages/CouncilHub"));
const CommunityPost = lazy(() => import("./pages/CommunityPost"));
const AdminCommunity = lazy(() => import("./pages/AdminCommunity"));
const GrantsPage = lazy(() => import("./pages/GrantsPage"));
const AdminGrants = lazy(() => import("./pages/AdminGrants"));
const AdminDatabase = lazy(() => import("./pages/AdminDatabase"));
const NucleusCapital = lazy(() => import("./pages/NucleusCapital"));
const NucleusVIP = lazy(() => import("./pages/NucleusVIP"));
const AdminIcf = lazy(() => import("./pages/AdminIcf"));
const NucleusPay = lazy(() => import("./pages/NucleusPay"));
const NucleusPayMerchant = lazy(() => import("./pages/NucleusPayMerchant"));
const AdminNucleusPay = lazy(() => import("./pages/AdminNucleusPay"));
const AdminPaymentLinks = lazy(() => import("./pages/AdminPaymentLinks"));
const AdminSchoolStudents = lazy(() => import("./pages/AdminSchoolStudents"));
const QuickPayLink = lazy(() => import("./pages/QuickPayLink"));
const Adhesion = lazy(() => import("./pages/Adhesion"));
const UboHome = lazy(() => import("./pages/ubo/Home"));
const UboStandard = lazy(() => import("./pages/ubo/Standard"));
const UboSchools = lazy(() => import("./pages/ubo/Schools"));
const UboFamilies = lazy(() => import("./pages/ubo/Families"));
const UboCouncil = lazy(() => import("./pages/ubo/Council"));
const UboEvidence = lazy(() => import("./pages/ubo/Evidence"));
const UboAbout = lazy(() => import("./pages/ubo/About"));
const UboFramework = lazy(() => import("./pages/ubo/Framework"));
const UboAuthorization = lazy(() => import("./pages/ubo/Authorization"));
const UboAssessment = lazy(() => import("./pages/ubo/Assessment"));
const UboEducators = lazy(() => import("./pages/ubo/Educators"));
const UboResearch = lazy(() => import("./pages/ubo/Research"));
const NucleusLive = lazy(() => import("./pages/NucleusLive"));
const AdminNucleusLive = lazy(() => import("./pages/AdminNucleusLive"));
const Resultats = lazy(() => import("./pages/Resultats"));
const AdminResultats = lazy(() => import("./pages/AdminResultats"));
const AdminSchoolApplications = lazy(() => import("./pages/AdminSchoolApplications"));
const UboInternational = lazy(() => import("./pages/UboInternational"));
const NucleusPayCheckout = lazy(() => import("./pages/NucleusPayCheckout"));
const NucleusPayReturn = lazy(() => import("./pages/NucleusPayReturn"));
const WireTransferPage = lazy(() => import("./pages/WireTransferPage"));
const AdminWireTransfers = lazy(() => import("./pages/AdminWireTransfers"));
const ReferralDashboard = lazy(() => import("./pages/ReferralDashboard"));
const WorldSchool = lazy(() => import("./pages/WorldSchool"));
const WebsitePreview = lazy(() => import("./pages/WebsitePreview"));
const ParentPay = lazy(() => import("./pages/ParentPay"));
const ParentPayReturn = lazy(() => import("./pages/ParentPayReturn"));
const NucleusPayResult = lazy(() => import("./pages/NucleusPayResult"));
const SchoolFees = lazy(() => import("./pages/SchoolFees"));
const BulkImportStudents = lazy(() => import("./pages/BulkImportStudents"));
const FamillesCompatibles = lazy(() => import("./pages/FamillesCompatibles"));
const AdminMarketplace = lazy(() => import("./pages/AdminMarketplace"));
const AdminProspectRouting = lazy(() => import("./pages/AdminProspectRouting"));
const AdminTiers = lazy(() => import("./pages/AdminTiers"));
const AdminAdvances = lazy(() => import("./pages/AdminAdvances"));
const AdminFeatureFlags = lazy(() => import("./pages/AdminFeatureFlags"));
const AdminDirector = lazy(() => import("./pages/AdminDirector"));
const AdminMatching = lazy(() => import("./pages/AdminMatching"));
const AdminFamilies = lazy(() => import("./pages/AdminFamilies"));
const AdmissionsQueue = lazy(() => import("./pages/AdmissionsQueue"));
const DiagnosticFlow = lazy(() => import("./pages/DiagnosticFlow"));
const DiagnosticReport = lazy(() => import("./pages/DiagnosticFlow").then((m) => ({ default: m.DiagnosticReport })));
const PeiEnrollment = lazy(() => import("./pages/PeiEnrollment"));
const AdminDiagnosticQuestions = lazy(() => import("./pages/AdminDiagnosticQuestions"));
const AdminFamilyAgents = lazy(() => import("./pages/AdminFamilyAgents"));
const AdminApplications = lazy(() => import("./pages/AdminApplications"));
const Messages = lazy(() => import("./pages/Messages"));
const ChoiceScreen = lazy(() => import("./pages/ChoiceScreen"));
const ParentSignup = lazy(() => import("./pages/ParentSignup"));
const ParentLogin = lazy(() => import("./pages/ParentLogin"));
const FamilyForgotPassword = lazy(() => import("./pages/FamilyForgotPassword"));
const FamilyResetPassword = lazy(() => import("./pages/FamilyResetPassword"));
const FamilleActivation = lazy(() => import("./pages/FamilleActivation"));
const FamilyDashboard = lazy(() => import("./pages/ParentPortal").then((m) => ({ default: m.ParentDashboard })));
const ApplicationForm = lazy(() => import("./pages/ParentPortal").then((m) => ({ default: m.ApplicationForm })));
const ApplicationDetail = lazy(() => import("./pages/ParentPortal").then((m) => ({ default: m.ApplicationDetail })));
const FamilyCouncil = lazy(() => import("./pages/FamilyCouncil"));

// Protected Route wrapper with Layout
const ProtectedRoute = ({ children, withLayout = true }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Also check for token in localStorage (for teacher/student/parent who don't use AuthContext)
  const hasToken = !!localStorage.getItem('nucleus_token');
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />;
  }
  
  if (withLayout) {
    return <AppLayout>{children}</AppLayout>;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-oxford-700 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function MetaTracker() {
  const location = useLocation();
  React.useEffect(() => { initPixel(); }, []);
  React.useEffect(() => {
    trackPageView();
    trackWelcomeIfPresent();
  }, [location.pathname, location.search]);
  return null;
}

function AppContent() {
  return (
    <BrowserRouter>
      <MetaTracker />
      <Navbar />
      <OfflineIndicator />
      <Suspense fallback={<div className="min-h-screen grid place-items-center bg-[#FAF7F0]"><div className="animate-spin w-8 h-8 border-4 border-[#0F1B3D] border-t-transparent rounded-full" /></div>}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<UboHome />} />
        <Route path="/standard" element={<UboStandard />} />
        <Route path="/schools" element={<UboSchools />} />
        <Route path="/families" element={<UboFamilies />} />
        <Route path="/council" element={<UboCouncil />} />
        <Route path="/evidence" element={<UboEvidence />} />
        <Route path="/about" element={<UboAbout />} />
        <Route path="/framework" element={<UboFramework />} />
        <Route path="/authorization" element={<UboAuthorization />} />
        <Route path="/assessment" element={<UboAssessment />} />
        <Route path="/educators" element={<UboEducators />} />
        <Route path="/research" element={<UboResearch />} />
        <Route path="/ecoles" element={<Navigate to="/schools" replace />} />
        <Route path="/familles" element={<Navigate to="/families" replace />} />
        <Route path="/familles/inscription" element={<ParentSignup />} />
        <Route path="/familles/connexion" element={<ParentLogin />} />
        <Route path="/familles/mot-de-passe-oublie" element={<FamilyForgotPassword />} />
        <Route path="/familles/reinitialiser" element={<FamilyResetPassword />} />
        <Route path="/familles/activer/:token" element={<FamilleActivation />} />
        <Route path="/portail" element={<FamilyDashboard />} />
        <Route path="/portail/candidatures/nouvelle" element={<ApplicationForm />} />
        <Route path="/portail/candidatures/:appId" element={<ApplicationDetail />} />
        <Route path="/portail/candidatures/:appId/modifier" element={<ApplicationForm />} />
        <Route path="/portail/communaute" element={<FamilyCouncil />} />
        <Route path="/portail/diagnostic/:appId" element={<DiagnosticFlow />} />
        <Route path="/portail/diagnostic/:appId/rapport" element={<DiagnosticReport />} />
        <Route path="/portail/pei/:appId" element={<PeiEnrollment />} />
        <Route path="/switch" element={<ChoiceScreen />} />
        <Route path="/landing-ecoles" element={<Landing />} />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={<Register />} 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
        
        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/edit" 
          element={
            <ProtectedRoute>
              <SchoolForm />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/inbox" 
          element={
            <ProtectedRoute>
              <Inbox />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/council" 
          element={<CouncilHub />}
        />
        <Route 
          path="/grants" 
          element={<GrantsPage />}
        />
        <Route 
          path="/admin/grants" 
          element={<AdminGrants />}
        />
        <Route
          path="/website/preview"
          element={<WebsitePreview />}
        />
        
        {/* Role-specific dashboards */}
        <Route 
          path="/teacher" 
          element={
            <ProtectedRoute>
              <TeacherDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/student" 
          element={
            <ProtectedRoute>
              <StudentDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/parent" 
          element={
            <ProtectedRoute>
              <ParentDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Parent account claim - public */}
        <Route path="/claim-account" element={<ClaimAccount />} />
        
        {/* Phase 1 - School Management Routes */}
        <Route 
          path="/school/setup" 
          element={
            <ProtectedRoute>
              <SchoolSetupWizard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/academic-years" 
          element={
            <ProtectedRoute>
              <AcademicYears />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/structure" 
          element={
            <ProtectedRoute>
              <SchoolStructure />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/students" 
          element={
            <ProtectedRoute>
              <EnhancedStudents />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/students/import-ia" 
          element={
            <ProtectedRoute>
              <BulkImportStudents />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/familles-compatibles" 
          element={
            <ProtectedRoute>
              <FamillesCompatibles />
            </ProtectedRoute>
          } 
        />
        <Route path="/admin/familles-compatibles" element={<AdminMarketplace />} />
        <Route path="/admin/prospect-routing" element={<AdminProspectRouting />} />
        <Route 
          path="/school/calendar" 
          element={
            <ProtectedRoute>
              <SchoolCalendar />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/audit" 
          element={
            <ProtectedRoute>
              <AuditLog />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/dashboard" 
          element={
            <ProtectedRoute>
              <EnhancedDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 2 - Daily Operations Routes */}
        <Route 
          path="/school/attendance" 
          element={
            <ProtectedRoute>
              <AttendanceMarking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/timetable" 
          element={
            <ProtectedRoute>
              <TimetableManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/communication" 
          element={
            <ProtectedRoute>
              <CommunicationHub />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/fees" 
          element={
            <ProtectedRoute>
              <SchoolFees />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/fees-legacy" 
          element={
            <ProtectedRoute>
              <FeeManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 3 - Academic Routes */}
        <Route 
          path="/school/gradebook" 
          element={
            <ProtectedRoute>
              <GradeBook />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/homework" 
          element={
            <ProtectedRoute>
              <HomeworkManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/courses" 
          element={
            <ProtectedRoute>
              <CourseContent />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/school/report-cards" 
          element={
            <ProtectedRoute>
              <ReportCards />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/student/homework" 
          element={
            <ProtectedRoute>
              <StudentHomework />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/student/grades" 
          element={
            <ProtectedRoute>
              <StudentGrades />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 4 - Quiz Routes */}
        <Route 
          path="/school/quizzes" 
          element={
            <ProtectedRoute>
              <QuizBuilder />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/student/quizzes" 
          element={
            <ProtectedRoute>
              <QuizTaking />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 4 - Offline Content */}
        <Route 
          path="/offline-content" 
          element={
            <ProtectedRoute>
              <OfflineContent />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 4 - Analytics */}
        <Route 
          path="/school/analytics" 
          element={
            <ProtectedRoute>
              <SchoolAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/teacher/analytics" 
          element={
            <ProtectedRoute>
              <TeacherAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/parent/progress" 
          element={
            <ProtectedRoute>
              <ParentProgressReport />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 4 - UBO Features */}
        <Route 
          path="/ubo" 
          element={
            <ProtectedRoute>
              <UBOCertification />
            </ProtectedRoute>
          } 
        />
        
        {/* Phase 5 - Data Export & Notifications */}
        <Route 
          path="/settings/export" 
          element={
            <ProtectedRoute>
              <DataExport />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings/notifications" 
          element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          } 
        />
        
        {/* Generated website - public */}
        <Route path="/schools/:slug" element={<GeneratedWebsite />} />
        
        {/* Admin Panel */}
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin/parent-inbox" element={<AdminParentInbox />} />

        {/* Parent leads chat (school side) */}
        <Route
          path="/parent-chat/:profileId"
          element={
            <ProtectedRoute>
              <ParentChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admissions"
          element={
            <ProtectedRoute>
              <AdmissionsOffice />
            </ProtectedRoute>
          }
        />
        <Route path="/community" element={<CouncilHub />} />
        <Route path="/community/post/:postId" element={<CommunityPost />} />
        <Route path="/admin/community" element={<AdminCommunity />} />
        <Route path="/admin/tiers" element={<AdminTiers />} />
        <Route path="/admin/database" element={<AdminDatabase />} />
        <Route path="/admin/database/:userId/students" element={<AdminSchoolStudents />} />
        <Route path="/nucleus-capital" element={<NucleusCapital />} />
        <Route path="/nucleus-capital/vip" element={<NucleusVIP />} />
        <Route path="/nucleus-capital/iaf" element={<NucleusCapital />} />
        <Route path="/admin/icf" element={<AdminIcf />} />
        <Route path="/nucleus-pay" element={<NucleusPay />} />
        <Route path="/nucleus-pay/merchant" element={<NucleusPayMerchant />} />
        <Route path="/nucleus-pay/checkout" element={<NucleusPayCheckout />} />
        <Route path="/nucleus-pay/checkout/return" element={<NucleusPayReturn />} />
        <Route path="/nucleus-pay/return" element={<NucleusPayReturn />} />
        <Route path="/nucleus-pay/success" element={<NucleusPayResult />} />
        <Route path="/nucleus-pay/cancel" element={<NucleusPayResult cancelled />} />
        <Route path="/nucleus-pay/wire" element={<WireTransferPage />} />
        <Route path="/admin/nucleus-pay" element={<AdminNucleusPay />} />
        <Route path="/admin/nucleus-pay/links" element={<AdminPaymentLinks />} />
        <Route path="/pay/link/:uuid" element={<QuickPayLink />} />
        <Route path="/adhesion" element={<Adhesion />} />
        <Route path="/live/:code" element={<NucleusLive />} />
        <Route path="/resultats" element={<Resultats />} />
        <Route path="/admin/resultats" element={<AdminResultats />} />
        <Route path="/admin/live" element={<AdminNucleusLive />} />
        <Route path="/admin/school-applications" element={<AdminSchoolApplications />} />
        <Route path="/ubo-international" element={<UboInternational />} />
        <Route path="/ubo-international/:sid" element={<UboInternational />} />
        <Route path="/admin/nucleus-pay-wire" element={<AdminWireTransfers />} />
        <Route path="/admin/advances" element={<AdminAdvances />} />
        <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
        <Route path="/admin/director" element={<AdminDirector />} />
        <Route path="/admin/matching" element={<AdminMatching />} />
        <Route path="/admin/families" element={<AdminFamilies />} />
        <Route path="/admin/family-agents" element={<AdminFamilyAgents />} />
        <Route path="/admin/applications" element={<AdminApplications />} />
        <Route path="/admin/admissions" element={<AdmissionsQueue />} />
        <Route path="/admin/diagnostic-questions" element={<AdminDiagnosticQuestions />} />
        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        <Route path="/messages/:threadId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
        {/* Public parent-facing payment pages (no auth required) */}
        <Route path="/pay/:sid" element={<ParentPay />} />
        <Route path="/pay/:sid/return" element={<ParentPayReturn />} />
        <Route path="/refer" element={<ReferralDashboard />} />
        <Route path="/world-school" element={<WorldSchool />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      
      <Toaster 
        position="top-right"
        richColors
        closeButton
      />
    </BrowserRouter>
  );
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <AuthProvider>
        <ParentAuthProvider>
          <UpgradeProvider>
            <ActivationProvider>
              <AppContent />
            </ActivationProvider>
          </UpgradeProvider>
        </ParentAuthProvider>
      </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
